import { appOrigin } from "./env";
import type { PersonalizationLevel } from "./types";

/**
 * Pure formatting and small browser utilities. No app state, no fetches.
 */

export function cleanPreviewText(value: string) {
  const text = value.replace(/\s+/g, " ").trim();
  return text.length > 190 ? `${text.slice(0, 187).trim()}...` : text;
}

export function formatElapsedSeconds(totalSeconds: number) {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;
  if (!minutes) return `${seconds}s`;
  return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
}

export function formatLevel(level: PersonalizationLevel) {
  const labels: Record<PersonalizationLevel, string> = {
    L0: "metadata-only",
    L1: "light rhetorical",
    L2: "moderate rhetorical",
    L3: "substantive-review"
  };
  return labels[level];
}

/**
 * `formatStatus` is DELETED. `status.replace(/_/g, " ")` printed "blocked for human" as if it
 * were a mood, and it gave every unmapped future state a benign-looking default. A machine
 * state now resolves through the exhaustive STATUS_META map in lib/status.ts, which fails the
 * BUILD if a state is unmapped. Non-status backend strings use humanizeToken() from the same
 * module — they still get their underscores softened, but they get no promise of meaning.
 */

export function formatDate(value: string) {
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  }).format(new Date(value));
}

export function formatBytes(value: number) {
  if (!value) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let size = value;
  let index = 0;
  while (size >= 1024 && index < units.length - 1) {
    size /= 1024;
    index += 1;
  }
  return `${size >= 10 || index === 0 ? Math.round(size) : size.toFixed(1)} ${units[index]}`;
}

export function sleep(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/**
 * The MCP connection string, always canonical. See `appOrigin` — this is pasted into an external
 * AI tool, so it must be the address that tool can reach, never the origin of the current tab.
 */
export function getMcpEndpoint() {
  return `${appOrigin}/mcp`;
}

export function maskMcpToken(token: string) {
  if (!token) return "No token";
  return `${token.slice(0, 18)}...${token.slice(-10)}`;
}

export async function copyToClipboard(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
}
